import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./FirstDepositBonus.css";


/* =========================================================
   COLLECTIONS
========================================================= */

const USDT_COLLECTION =
  "usdt_transactions";

const VISITOR_COLLECTION =
  "visterdeposte";

const USERS_COLLECTION =
  "users";


/* =========================================================
   FIRST USDT BONUS SETTINGS
========================================================= */

/*
  Minimum deposit required.
*/

const FIRST_USDT_MINIMUM =
  10;


/*
  Bonus tiers:

  10 USDT - 999.99 USDT
  = 10%

  1,000 USDT - 2,999.99 USDT
  = 8%

  3,000 USDT+
  = 6%
*/


/* =========================================================
   NORMALIZE USERNAME
========================================================= */

const normalizeUsername = (
  username
) => {

  return String(
    username || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  return String(
    status || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE POSITION
========================================================= */

const normalizePosition = (
  position
) => {

  return String(
    position || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   FORMAT USDT
========================================================= */

const formatUSDT = (
  amount
) => {

  const value =
    Number(
      amount || 0
    );


  return `${value.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )} USDT`;

};


/* =========================================================
   TIMESTAMP → SECONDS
========================================================= */

const getTimestampSeconds = (
  item
) => {

  const value =
    item?.createdAt ||
    item?.timestamp ||
    item?.paidAt ||
    item?.updatedAt;


  if (
    !value
  ) {

    return 0;

  }


  if (
    typeof value?.seconds ===
    "number"
  ) {

    return Number(
      value.seconds
    );

  }


  if (
    typeof value?.toDate ===
    "function"
  ) {

    const date =
      value.toDate();


    if (
      date instanceof Date &&
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return Math.floor(
        date.getTime() /
        1000
      );

    }

  }


  if (
    typeof value ===
    "number"
  ) {

    return value >
      100000000000

      ? Math.floor(
          value / 1000
        )

      : Math.floor(
          value
        );

  }


  const parsed =
    new Date(
      value
    ).getTime();


  if (
    Number.isNaN(
      parsed
    )
  ) {

    return 0;

  }


  return Math.floor(
    parsed /
    1000
  );

};


/* =========================================================
   GET DEPOSIT POSITION
========================================================= */

const getDepositPosition = (
  deposit
) => {

  return normalizePosition(
    deposit?.position ||
    deposit?.depositOrderType ||
    ""
  );

};


/* =========================================================
   GET DEPOSIT CURRENCY
========================================================= */

const getDepositCurrency = (
  deposit
) => {

  /*
    Dedicated USDT collection.
  */

  if (
    deposit?.source ===
    USDT_COLLECTION
  ) {

    return "USDT";

  }


  /*
    Explicit currency.
  */

  if (
    deposit?.currency
  ) {

    return String(
      deposit.currency
    )
      .trim()
      .toUpperCase();

  }


  /*
    Source currency.
  */

  if (
    deposit?.sourceCurrency
  ) {

    return String(
      deposit.sourceCurrency
    )
      .trim()
      .toUpperCase();

  }


  return "";

};


/* =========================================================
   GET USER BALANCE FIELD
========================================================= */

const getUSDTBalanceField = () => {

  /*
    IMPORTANT:

    Firebase field is exactly:

    usdtBalance

    NOT:

    USDTBalance
  */

  return "usdtBalance";

};


/* =========================================================
   FIND USER BY USERNAME
========================================================= */

const findUserByUsername = async (
  username
) => {

  if (
    !username
  ) {

    return null;

  }


  const usersRef =
    collection(
      db,
      USERS_COLLECTION
    );


  /*
    Exact search first.
  */

  try {

    const exactQuery =
      query(

        usersRef,

        where(
          "username",
          "==",
          username
        )

      );


    const exactSnapshot =
      await getDocs(
        exactQuery
      );


    if (
      !exactSnapshot.empty
    ) {

      return exactSnapshot.docs[0];

    }

  }

  catch (
    error
  ) {

    console.warn(
      "Exact username search failed:",
      error
    );

  }


  /*
    Case-insensitive fallback.
  */

  const allUsers =
    await getDocs(
      usersRef
    );


  const normalized =
    normalizeUsername(
      username
    );


  return (

    allUsers.docs.find(
      userDoc =>

        normalizeUsername(
          userDoc.data()?.username
        ) ===
        normalized

    ) || null

  );

};


/* =========================================================
   FIRST USDT BONUS CALCULATION
========================================================= */

const getFirstUSDTBonusData = (
  depositAmount
) => {

  const amount =
    Number(
      depositAmount || 0
    );


  /*
    Below minimum.
  */

  if (
    amount <
    FIRST_USDT_MINIMUM
  ) {

    return {

      locked:
        true,

      percent:
        0,

      amount:
        0,

      label:
        `Locked - Min ${FIRST_USDT_MINIMUM} USDT`,

      bonusType:
        "first_usdt",

    };

  }


  /*
    10 - 999.99 USDT
    = 10%
  */

  if (
    amount <
    1000
  ) {

    return {

      locked:
        false,

      percent:
        10,

      amount:
        Number(
          (
            amount *
            0.10
          ).toFixed(2)
        ),

      label:
        "First USDT Deposit 10%",

      bonusType:
        "first_usdt",

    };

  }


  /*
    1,000 - 2,999.99 USDT
    = 8%
  */

  if (
    amount <
    3000
  ) {

    return {

      locked:
        false,

      percent:
        8,

      amount:
        Number(
          (
            amount *
            0.08
          ).toFixed(2)
        ),

      label:
        "First USDT Deposit 8%",

      bonusType:
        "first_usdt",

    };

  }


  /*
    3,000+ USDT
    = 6%
  */

  return {

    locked:
      false,

    percent:
      6,

    amount:
      Number(
        (
          amount *
          0.06
        ).toFixed(2)
      ),

    label:
      "First USDT Deposit 6%",

    bonusType:
      "first_usdt",

  };

};


/* =========================================================
   GET FIRST USDT DEPOSIT
========================================================= */

const getFirstUSDTDeposit =
  async (
    username
  ) => {

    if (
      !username
    ) {

      return null;

    }


    /*
      =======================================================
      USDT TRANSACTIONS
      =======================================================
    */

    let usdtDeposits = [];


    try {

      const usdtQuery =
        query(

          collection(
            db,
            USDT_COLLECTION
          ),

          where(
            "username",
            "==",
            username
          ),

          where(
            "status",
            "==",
            "success"
          )

        );


      const usdtSnapshot =
        await getDocs(
          usdtQuery
        );


      usdtDeposits =
        usdtSnapshot.docs.map(
          depositDoc => {

            const data =
              depositDoc.data();


            return {

              id:
                depositDoc.id,

              ...data,

              source:
                USDT_COLLECTION,

              currency:
                "USDT",

              originalAmount:
                Number(
                  data.amount || 0
                ),

            };

          }
        );

    }

    catch (
      error
    ) {

      console.warn(
        "USDT collection search failed:",
        error
      );

    }


    /*
      =======================================================
      VISITOR DEPOSITS
      =======================================================
    */

    let visitorDeposits = [];


    try {

      const visitorQuery =
        query(

          collection(
            db,
            VISITOR_COLLECTION
          ),

          where(
            "username",
            "==",
            username
          ),

          where(
            "status",
            "==",
            "success"
          )

        );


      const visitorSnapshot =
        await getDocs(
          visitorQuery
        );


      visitorDeposits =
        visitorSnapshot.docs.map(
          depositDoc => {

            const data =
              depositDoc.data();


            const currency =
              getDepositCurrency({

                ...data,

                source:
                  VISITOR_COLLECTION,

              });


            return {

              id:
                depositDoc.id,

              ...data,

              source:
                VISITOR_COLLECTION,

              currency,

              originalAmount:
                Number(
                  data.amount || 0
                ),

            };

          }
        );

    }

    catch (
      error
    ) {

      console.warn(
        "Visitor deposit search failed:",
        error
      );

    }


    /*
      =======================================================
      COMBINE
      =======================================================
    */

    const allDeposits = [

      ...usdtDeposits,

      ...visitorDeposits,

    ];


    /*
      =======================================================
      ONLY USDT
      =======================================================
    */

    const validDeposits =
      allDeposits.filter(
        deposit => {

          const currency =
            getDepositCurrency(
              deposit
            );


          return (

            normalizeUsername(
              deposit.username
            ) ===
            normalizeUsername(
              username
            ) &&

            normalizeStatus(
              deposit.status
            ) ===
            "success" &&

            currency ===
            "USDT" &&

            Number(
              deposit.originalAmount ||
              0
            ) > 0

          );

        }
      );


    /*
      =======================================================
      OLDEST FIRST
      =======================================================
    */

    validDeposits.sort(
      (
        a,
        b
      ) =>

        getTimestampSeconds(a) -
        getTimestampSeconds(b)

    );


    /*
      =======================================================
      EXPLICIT FIRST
      =======================================================
    */

    const explicitFirst =
      validDeposits.find(
        deposit =>

          getDepositPosition(
            deposit
          ) ===
          "first"

      );


    if (
      explicitFirst
    ) {

      return explicitFirst;

    }


    /*
      =======================================================
      LEGACY RECORD
      =======================================================
    */

    const legacy =
      validDeposits.find(
        deposit =>

          getDepositPosition(
            deposit
          ) ===
          ""

      );


    if (
      legacy
    ) {

      return legacy;

    }


    /*
      =======================================================
      EARLIEST SUCCESSFUL USDT DEPOSIT
      =======================================================
    */

    return (
      validDeposits[0] ||
      null
    );

  };


/* =========================================================
   COMPONENT
========================================================= */

export default function FirstDepositBonusUSDT() {

  const navigate =
    useNavigate();


  /* =======================================================
     USERNAME
  ======================================================= */

  const [
    username,
    setUsername,
  ] = useState("");


  /* =======================================================
     BONUS
  ======================================================= */

  const [
    bonus,
    setBonus,
  ] = useState(0);


  /* =======================================================
     DEPOSIT AMOUNT
  ======================================================= */

  const [
    depositAmount,
    setDepositAmount,
  ] = useState(0);


  /* =======================================================
     BONUS PERCENT
  ======================================================= */

  const [
    bonusPercent,
    setBonusPercent,
  ] = useState(0);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     APPLYING
  ======================================================= */

  const [
    applying,
    setApplying,
  ] = useState(false);


  /* =======================================================
     COMPLETED
  ======================================================= */

  const [
    completed,
    setCompleted,
  ] = useState(false);


  /* =======================================================
     MESSAGE
  ======================================================= */

  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     LOAD BONUS
  ======================================================= */

  useEffect(
    () => {

      let mounted =
        true;


      const loadBonus =
        async () => {

          try {

            if (
              mounted
            ) {

              setLoading(
                true
              );

            }


            /*
              =================================================
              LOGIN
              =================================================
            */

            const storedUsername =
              localStorage.getItem(
                "username"
              );


            if (
              !storedUsername
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Login required."
                );

              }

              return;

            }


            if (
              mounted
            ) {

              setUsername(
                storedUsername
              );

            }


            /*
              =================================================
              FIND USER
              =================================================
            */

            const userDoc =
              await findUserByUsername(
                storedUsername
              );


            if (
              !userDoc
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Account not found."
                );

              }

              return;

            }


            /*
              =================================================
              FIND FIRST USDT DEPOSIT
              =================================================
            */

            const firstDeposit =
              await getFirstUSDTDeposit(
                storedUsername
              );


            if (
              !firstDeposit
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "First USDT deposit haijapatikana."
                );

              }

              return;

            }


            /*
              =================================================
              CHECK POSITION
              =================================================
            */

            const position =
              getDepositPosition(
                firstDeposit
              );


            if (
              position &&
              position !==
              "first"
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Deposit hii si First USDT Deposit."
                );

              }

              return;

            }


            /*
              =================================================
              CHECK ALREADY CLAIMED
              =================================================
            */

            if (
              firstDeposit.selfBonusGiven ===
              true
            ) {

              if (
                mounted
              ) {

                setCompleted(
                  true
                );

                setMessage(
                  "First USDT Bonus tayari imetolewa."
                );

              }

              return;

            }


            /*
              =================================================
              VERIFY CURRENCY
              =================================================
            */

            const depositCurrency =
              getDepositCurrency(
                firstDeposit
              );


            if (
              depositCurrency !==
              "USDT"
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "Deposit hii si ya USDT."
                );

              }

              return;

            }


            /*
              =================================================
              DEPOSIT AMOUNT
              =================================================
            */

            const amount =
              Number(
                firstDeposit.originalAmount ||
                firstDeposit.amount ||
                0
              );


            if (
              amount <=
              0
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "USDT deposit amount haijapatikana."
                );

              }

              return;

            }


            if (
              mounted
            ) {

              setDepositAmount(
                amount
              );

            }


            /*
              =================================================
              MINIMUM
              =================================================
            */

            if (
              amount <
              FIRST_USDT_MINIMUM
            ) {

              if (
                mounted
              ) {

                setMessage(
                  `Deposit yako ni ${formatUSDT(
                    amount
                  )}. Minimum kwa First USDT Bonus ni ${FIRST_USDT_MINIMUM} USDT.`
                );

              }

              return;

            }


            /*
              =================================================
              CALCULATE BONUS
              =================================================
            */

            const bonusData =
              getFirstUSDTBonusData(
                amount
              );


            if (
              bonusData.locked ||
              bonusData.amount <=
              0
            ) {

              if (
                mounted
              ) {

                setMessage(
                  "First USDT Bonus haijafuzu."
                );

              }

              return;

            }


            /*
              =================================================
              SET UI
              =================================================
            */

            if (
              mounted
            ) {

              setBonus(
                bonusData.amount
              );

              setBonusPercent(
                bonusData.percent
              );

              setMessage(
                ""
              );

            }

          }

          catch (
            error
          ) {

            console.error(
              "First USDT Bonus loading error:",
              error
            );


            if (
              mounted
            ) {

              setMessage(
                error?.message ||
                "Unable to load First USDT Bonus."
              );

            }

          }

          finally {

            if (
              mounted
            ) {

              setLoading(
                false
              );

            }

          }

        };


      loadBonus();


      return () => {

        mounted =
          false;

      };

    },
    []
  );


  /* =======================================================
     APPLY BONUS
  ======================================================= */

  const applyBonus =
    async () => {

      if (
        applying ||
        completed ||
        !bonus ||
        !username
      ) {

        return;

      }


      setApplying(
        true
      );


      setMessage(
        ""
      );


      try {

        /*
          ===================================================
          FIND USER
          ===================================================
        */

        const userDoc =
          await findUserByUsername(
            username
          );


        if (
          !userDoc
        ) {

          throw new Error(
            "User account not found."
          );

        }


        /*
          ===================================================
          FIND FIRST USDT DEPOSIT
          ===================================================
        */

        const deposit =
          await getFirstUSDTDeposit(
            username
          );


        if (
          !deposit
        ) {

          throw new Error(
            "First USDT deposit not found."
          );

        }


        /*
          ===================================================
          DEPOSIT REFERENCE
          ===================================================
        */

        const depositRef =
          doc(
            db,
            deposit.source,
            deposit.id
          );


        /*
          ===================================================
          FINAL BONUS
          ===================================================
        */

        let finalBonus =
          0;


        /*
          ===================================================
          TRANSACTION
          ===================================================
        */

        await runTransaction(
          db,
          async (
            transaction
          ) => {

            /*
              ===============================================
              READ DEPOSIT
              ===============================================
            */

            const depositSnapshot =
              await transaction.get(
                depositRef
              );


            if (
              !depositSnapshot.exists()
            ) {

              throw new Error(
                "USDT deposit transaction not found."
              );

            }


            const freshDeposit =
              depositSnapshot.data();


            /*
              ===============================================
              SUCCESS CHECK
              ===============================================
            */

            if (
              normalizeStatus(
                freshDeposit.status
              ) !==
              "success"
            ) {

              throw new Error(
                "USDT deposit is not successful."
              );

            }


            /*
              ===============================================
              CURRENCY CHECK
              ===============================================
            */

            const freshCurrency =
              getDepositCurrency({

                ...freshDeposit,

                source:
                  deposit.source,

              });


            if (
              freshCurrency !==
              "USDT"
            ) {

              throw new Error(
                "Deposit hii si ya USDT."
              );

            }


            /*
              ===============================================
              POSITION CHECK
              ===============================================
            */

            const freshPosition =
              getDepositPosition(
                freshDeposit
              );


            if (
              freshPosition &&
              freshPosition !==
              "first"
            ) {

              throw new Error(
                "Deposit hii si First USDT Deposit."
              );

            }


            /*
              ===============================================
              DOUBLE CLAIM PROTECTION
              ===============================================
            */

            if (
              freshDeposit.selfBonusGiven ===
              true
            ) {

              throw new Error(
                "First USDT Bonus tayari imetolewa."
              );

            }


            /*
              ===============================================
              USERNAME CHECK
              ===============================================
            */

            const depositUsername =
              normalizeUsername(
                freshDeposit.username
              );


            const currentUsername =
              normalizeUsername(
                username
              );


            if (
              depositUsername &&
              depositUsername !==
              currentUsername
            ) {

              throw new Error(
                "Deposit username haiendani na account hii."
              );

            }


            /*
              ===============================================
              READ USER
              ===============================================
            */

            const userSnapshot =
              await transaction.get(
                userDoc.ref
              );


            if (
              !userSnapshot.exists()
            ) {

              throw new Error(
                "User account not found."
              );

            }


            const userData =
              userSnapshot.data();


            /*
              ===============================================
              BALANCE FIELD

              IMPORTANT:
              usdtBalance
              ===============================================
            */

            const userBalanceField =
              getUSDTBalanceField();


            /*
              ===============================================
              DEPOSIT AMOUNT
              ===============================================
            */

            const amount =
              Number(
                freshDeposit.amount ||
                0
              );


            if (
              amount <=
              0
            ) {

              throw new Error(
                "USDT deposit amount haijapatikana."
              );

            }


            /*
              ===============================================
              MINIMUM
              ===============================================
            */

            if (
              amount <
              FIRST_USDT_MINIMUM
            ) {

              throw new Error(
                `Deposit hii ni ${formatUSDT(
                  amount
                )}. Minimum ni ${FIRST_USDT_MINIMUM} USDT.`
              );

            }


            /*
              ===============================================
              BONUS CALCULATION
              ===============================================
            */

            const bonusData =
              getFirstUSDTBonusData(
                amount
              );


            if (
              bonusData.locked ||
              bonusData.amount <=
              0
            ) {

              throw new Error(
                "First USDT Bonus haijafuzu."
              );

            }


            const calculatedBonus =
              Number(
                bonusData.amount
              );


            /*
              ===============================================
              CURRENT BALANCE
              ===============================================
            */

            const currentBalance =
              Number(
                userData[
                  userBalanceField
                ] || 0
              );


            /*
              ===============================================
              NEW BALANCE
              ===============================================
            */

            const newBalance =
              Number(
                (
                  currentBalance +
                  calculatedBonus
                ).toFixed(2)
              );


            /*
              ===============================================
              UPDATE USER

              ONLY:

              usdtBalance
              ===============================================
            */

            transaction.update(
              userDoc.ref,
              {

                usdtBalance:
                  newBalance,

              }
            );


            /*
              ===============================================
              UPDATE DEPOSIT

              BONUS INFORMATION STORED HERE
              ===============================================
            */

            const depositUpdate = {

              /*
                ---------------------------------------------
                USER BONUS
                ---------------------------------------------
              */

              selfBonusGiven:
                true,

              selfBonusClaimed:
                true,

              adminBonusGiven:
                true,

              selfBonusClaimedAmount:
                calculatedBonus,

              selfBonusAmount:
                calculatedBonus,

              selfBonusCurrency:
                "USDT",

              selfBonusPercent:
                Number(
                  bonusData.percent ||
                  0
                ),

              selfBonusLabel:
                bonusData.label,

              selfBonusType:
                "first_usdt",

              selfBonusLocked:
                false,

              selfBonusApprovedBy:
                "user",

              selfBonusApprovedAt:
                serverTimestamp(),


              /*
                ---------------------------------------------
                DEPOSIT INFORMATION
                ---------------------------------------------
              */

              bonusDepositAmount:
                amount,

              bonusDepositCurrency:
                "USDT",

              bonusBalanceField:
                "usdtBalance",

              bonusCurrency:
                "USDT",

              bonusStatus:
                "credited",

              bonusTransactionType:
                "depositBonus",

              bonusDepositOrder:
                "first",

              depositOrderType:
                "first",

              position:
                "first",

            };


            /*
              ===============================================
              SAVE BONUS ON DEPOSIT DOCUMENT
              ===============================================
            */

            transaction.update(
              depositRef,
              depositUpdate
            );


            /*
              ===============================================
              FINAL UI VALUE
              ===============================================
            */

            finalBonus =
              calculatedBonus;

          }
        );


        /*
          ===================================================
          SUCCESS
          ===================================================
        */

        setCompleted(
          true
        );


        setBonus(
          finalBonus
        );


        setMessage(
          `Hongera! ${formatUSDT(
            finalBonus
          )} imeongezwa kwenye usdtBalance yako.`
        );


        console.log(
          "FIRST USDT BONUS COMPLETED",
          {

            username,

            depositAmount,

            bonus:
              finalBonus,

            currency:
              "USDT",

            balanceField:
              "usdtBalance",

          }
        );

      }

      catch (
        error
      ) {

        console.error(
          "First USDT Bonus payment failed:",
          error
        );


        setMessage(
          error?.message ||
          "USDT bonus payment failed."
        );

      }

      finally {

        setApplying(
          false
        );

      }

    };


  /* =======================================================
     LOADING SCREEN
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="fdusdt-page">

        <button
          type="button"
          className="fdusdt-back-btn"
          onClick={() =>
            navigate(-1)
          }
        >

          <span className="fdusdt-back-icon">
            ←
          </span>

          <span>
            Back
          </span>

        </button>


        <div
          className="
            fdusdt-card
            fdusdt-loading
          "
        >

          <div className="fdusdt-loader" />

          <p>
            Checking your USDT bonus...
          </p>

        </div>

      </div>

    );

  }


  /* =======================================================
     MAIN RENDER
  ======================================================= */

  return (

    <div className="fdusdt-page">


      {/* ===================================================
          BACK BUTTON
      =================================================== */}

      <button
        type="button"
        className="fdusdt-back-btn"
        onClick={() =>
          navigate(-1)
        }
        aria-label="Go back"
      >

        <span className="fdusdt-back-icon">
          ←
        </span>

        <span>
          Back
        </span>

      </button>


      {/* ===================================================
          CARD
      =================================================== */}

      <div className="fdusdt-card">


        {/* =================================================
            GIFT ICON
        ================================================= */}

        <div className="fdusdt-gift">
          🎁
        </div>


        {/* =================================================
            BADGE
        ================================================= */}

        <div className="fdusdt-badge">
          FIRST USDT DEPOSIT
        </div>


        {/* =================================================
            TITLE
        ================================================= */}

        <h2>
          USDT Welcome Bonus
        </h2>


        {/* =================================================
            SUBTITLE
        ================================================= */}

        <p className="fdusdt-subtitle">

          Hongera{" "}

          <strong>
            {username || "Trader"}
          </strong>

          !

          <br />

          Umefuzu kupata
          First USDT Deposit Bonus.

        </p>


        {/* =================================================
            DEPOSIT
        ================================================= */}

        {depositAmount > 0 && (

          <div className="fdusdt-deposit-box">

            <span>
              FIRST USDT DEPOSIT
            </span>

            <strong>
              {formatUSDT(
                depositAmount
              )}
            </strong>

          </div>

        )}


        {/* =================================================
            BONUS
        ================================================= */}

        {bonus > 0 && (

          <div className="fdusdt-bonus-box">

            <span>
              YOUR BONUS
            </span>


            <strong>
              {formatUSDT(
                bonus
              )}
            </strong>


            <small>
              First USDT Deposit Bonus{" "}
              {bonusPercent}%
            </small>

          </div>

        )}


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (

          <div
            className={`
              fdusdt-message
              ${
                completed
                  ? "fdusdt-success"
                  : "fdusdt-info"
              }
            `}
          >

            <span>

              {
                completed
                  ? "✓"
                  : "!"
              }

            </span>


            <p>
              {message}
            </p>

          </div>

        )}


        {/* =================================================
            APPLY BUTTON
        ================================================= */}

        {bonus > 0 &&
        !completed && (

          <button
            type="button"
            className="fdusdt-apply-btn"
            onClick={
              applyBonus
            }
            disabled={
              applying
            }
          >

            {applying ? (

              <>

                <span
                  className="
                    fdusdt-button-loader
                  "
                />

                Processing...

              </>

            ) : (

              <>

                APPLY USDT BONUS NOW

                <span>
                  →
                </span>

              </>

            )}

          </button>

        )}


        {/* =================================================
            COMPLETED
        ================================================= */}

        {completed && (

          <div className="fdusdt-paid">

            <span>
              ✓
            </span>

            Bonus Already Claimed

          </div>

        )}


        {/* =================================================
            NOTE
        ================================================= */}

        <p className="fdusdt-note">

          Bonus yako itaongezwa moja kwa moja
          kwenye{" "}

          <strong>
            usdtBalance
          </strong>

          {" "}baada ya kubonyeza
          Apply USDT Bonus Now.

        </p>


      </div>

    </div>

  );

}
